/**
 * 保存済みの行から組み立てる見方（変更一覧・連携マトリクス・判定マトリクス）。
 *
 * **変更一覧は別の表として保存しない。** 画面・エンティティ・API の行の「変更」の列から毎回
 * 組み立てる（Markdown の書き出しと同じ組み立て方）。連携マトリクスと判定マトリクスも、
 * 実現方針と判定ロジックの行を並べ替えただけのもの。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の行から変更一覧・サービスの連携マトリクス・ケース×状態の判定マトリクスを組み立てる
 * @graph-connects none
 */

import { bdCell, splitRefs } from './bd-defs.js';
import type { BdArtifact, BdRow } from '../../../lib/api/basic-design.js';

/** 変更一覧の 1 行。 @graph-connects none */
export interface BdChangeRow {
  artifact: 'screens' | 'entities' | 'api_contracts';
  id: string;
  kind: 'screen' | 'entity' | 'api';
  name: string;
  /** 保存した値（`new`・`modified`・`removed`） */
  impact: string;
  decisions: string[];
}

/** 変更一覧に載せる変更（エンティティの「変更なし」は載せない）。 @graph-connects none */
const CHANGE_IMPACTS = new Set(['new', 'modified', 'removed']);

/** セルの値。 @graph-connects none */
function cell(row: BdRow, key: string): string {
  return bdCell(row, key).trim();
}

/** 重なりを除き、最初に現れた順に並べる。 @graph-connects none */
function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/** 1 つの節の行を変更一覧の行にする。 @graph-connects none */
function changeRows(
  artifact: BdChangeRow['artifact'],
  kind: BdChangeRow['kind'],
  rows: readonly BdRow[],
  nameOf: (row: BdRow) => string,
): BdChangeRow[] {
  return rows
    .filter((row) => CHANGE_IMPACTS.has(cell(row, 'impact')))
    .map((row) => ({
      artifact,
      id: cell(row, 'id'),
      kind,
      name: nameOf(row),
      impact: cell(row, 'impact'),
      decisions: unique(splitRefs(cell(row, 'decisions'))),
    }));
}

/** 画面 → エンティティ → API の順に、変更のある行を並べる。 @graph-connects none */
export function bdChangeList(rowsOf: (artifact: BdArtifact) => readonly BdRow[]): BdChangeRow[] {
  return [
    ...changeRows('screens', 'screen', rowsOf('screens'), (row) => cell(row, 'screen')),
    ...changeRows('entities', 'entity', rowsOf('entities'), (row) => cell(row, 'entity')),
    ...changeRows('api_contracts', 'api', rowsOf('api_contracts'), (row) =>
      `${cell(row, 'method')} ${cell(row, 'path')}`.trim(),
    ),
  ];
}

/** 連携マトリクスの 1 マス（起点 → 相手）。 @graph-connects none */
export interface BdIntegrationCell {
  from: string;
  to: string;
  mechanisms: string[];
  ids: string[];
}

/** 連携マトリクス。 @graph-connects none */
export interface BdIntegrationMatrix {
  callers: string[];
  callees: string[];
  cells: BdIntegrationCell[];
}

/** 値が空でなく、まだ無ければ足す。 @graph-connects none */
function pushDistinct(values: string[], value: string): void {
  if (value !== '' && !values.includes(value)) values.push(value);
}

/**
 * 実現方針の行を、起点 × 相手のマトリクスにする。起点か相手が空の行は外す。並びは最初に現れた順。
 * @graph-connects none
 */
export function bdIntegrationMatrix(approaches: readonly BdRow[]): BdIntegrationMatrix {
  const cells = new Map<string, BdIntegrationCell>();
  for (const row of approaches) {
    const from = cell(row, 'from');
    const to = cell(row, 'to');
    if (from === '' || to === '') continue;
    const key = JSON.stringify([from, to]);
    const found = cells.get(key) ?? { from, to, mechanisms: [], ids: [] };
    cells.set(key, found);
    pushDistinct(found.mechanisms, cell(row, 'mechanism'));
    pushDistinct(found.ids, cell(row, 'id'));
  }
  const list = [...cells.values()];
  return {
    callers: unique(list.map((c) => c.from)),
    callees: unique(list.map((c) => c.to)),
    cells: list,
  };
}

/** 判定マトリクスの 1 マス。`ids` と `outcomes` は同じ行の順に並ぶ。 @graph-connects none */
export interface BdDecisionCell {
  case: string;
  state: string;
  ids: string[];
  /** 結果。条件がある行は `条件 → 結果` */
  outcomes: string[];
}

/** 判定マトリクス。 @graph-connects none */
export interface BdDecisionMatrix {
  cases: string[];
  states: string[];
  cells: BdDecisionCell[];
}

/**
 * 表記の揺れ（大文字小文字・空白）を同じものとして扱い、最初に現れた書き方を返す
 * （API の判定ロジックのチェックと同じ比べ方）。
 * @graph-connects none
 */
function canonicalLabel(labels: Map<string, string>, text: string): string {
  const key = text.replace(/\s+/g, ' ').toLowerCase();
  const known = labels.get(key);
  if (known !== undefined) return known;
  labels.set(key, text);
  return text;
}

/**
 * 判定ロジックの行を、ケース × 状態のマトリクスにする。ケースか状態が空の行は外す。
 * @graph-connects canonicalLabel [calls] 表記の揺れの吸収
 */
export function bdDecisionMatrix(decisionLogic: readonly BdRow[]): BdDecisionMatrix {
  const cases = new Map<string, string>();
  const states = new Map<string, string>();
  const cells = new Map<string, BdDecisionCell>();
  for (const row of decisionLogic) {
    const caseText = cell(row, 'case');
    const stateText = cell(row, 'state');
    if (caseText === '' || stateText === '') continue;
    const caseLabel = canonicalLabel(cases, caseText);
    const stateLabel = canonicalLabel(states, stateText);
    const key = JSON.stringify([caseLabel, stateLabel]);
    const found = cells.get(key) ?? { case: caseLabel, state: stateLabel, ids: [], outcomes: [] };
    cells.set(key, found);
    const guard = cell(row, 'guard');
    const outcome = cell(row, 'outcome');
    found.ids.push(cell(row, 'id'));
    found.outcomes.push(guard === '' ? outcome : `${guard} → ${outcome}`);
  }
  return { cases: [...cases.values()], states: [...states.values()], cells: [...cells.values()] };
}

/** マトリクスのマスを引く。 @graph-connects none */
export function matrixCell<C extends { case: string; state: string }>(
  cells: readonly C[],
  caseLabel: string,
  state: string,
): C | undefined {
  return cells.find((c) => c.case === caseLabel && c.state === state);
}
